import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToastDefault } from "@/hooks/use-toast-default";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { FormInput } from "@/components/forms/input";
import { useCreateBusinessReportMutation, useUpdateBusinessReportMutation } from "../../businessReports.api";
import { BusinessReport } from "@/modules/snapshots/businessReports.types";

interface CreateBusinessReportModalProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  businessReport?: BusinessReport;
}

interface FormInputs {
  name: string;
  description: string;
  file: FileList;
}

export const CreateBusinessReportModal: React.FC<CreateBusinessReportModalProps> = ({
  isOpen,
  setIsOpen,
  businessReport,
}) => {
  const { showToast } = useToastDefault();

  const methods = useForm<FormInputs>({
    defaultValues: {
      name: businessReport?.name ?? "",
      description: businessReport?.description ?? "",
    },
  });

  const [createBusinessReport] = useCreateBusinessReportMutation();
  const [updateBusinessReport] = useUpdateBusinessReportMutation();

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    try {
      if (!businessReport) {
        await createBusinessReport({ data: payload }).unwrap();
      } else {
        await updateBusinessReport({ data: payload, id: businessReport.id }).unwrap();
      }

      setIsOpen(false);
      showToast("Success", businessReport ? "SnapShot template updated" : "SnapShot template created");
    } catch (error) {
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Create SnapShot template</DialogTitle>
            </DialogHeader>

            <FormInput name="name" placeholder="Enter name" className="my-3" />
            <FormInput name="description" placeholder="Enter descriptions" className="my-3" />
            {!businessReport && (
              <FormInput
                name="file"
                type="file"
                className="mb-1 mt-3"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            )}

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
